import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Tag, Space, Select, Typography } from "antd";
import { ShopOutlined, EnvironmentOutlined, CloudDownloadOutlined } from "@ant-design/icons";
import PageHeader from "./common/PageHeader";
import DataTable from "./common/DataTable";
import { listShops, syncShopsFromSource, getShopFacets } from "../network/api";
import { useAuth } from "../context/AuthContext";
import { notify, serverError } from "../utils/notify";
import { relativeTime } from "../constants/health";

const { Text } = Typography;

const STATUS_OPTIONS = [
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

export default function Shops() {
  const navigate = useNavigate();
  const { isHardwareAdmin } = useAuth();
  const [refreshKey, setRefreshKey] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const [facets, setFacets] = useState({ cities: [], regions: [], channels: [] });
  const [region, setRegion] = useState();
  const [channel, setChannel] = useState();
  const [status, setStatus] = useState();

  const open = (id) => navigate(`/dashboard/shop-details/${id}`);

  const loadFacets = async () => {
    try {
      setFacets(await getShopFacets());
    } catch (e) {
      /* non-fatal — dropdowns just stay empty */
    }
  };
  useEffect(() => {
    loadFacets();
  }, [refreshKey]);

  const onSync = async () => {
    setSyncing(true);
    try {
      const r = await syncShopsFromSource();
      notify.success(`Synced ${r.fetched} outlets (${r.created} new, ${r.updated} updated)`);
      setRefreshKey((k) => k + 1);
    } catch (error) {
      notify.error(serverError(error, "Failed to sync outlets from Planomaster"));
    } finally {
      setSyncing(false);
    }
  };

  const columns = [
    { title: "Code", dataIndex: "code", sorter: true, render: (v) => <Text strong>{v}</Text> },
    { title: "Outlet", dataIndex: "title", sorter: true },
    {
      title: "Location",
      key: "location",
      render: (_, r) =>
        r.city || r.region ? (
          <Space size={4}>
            <EnvironmentOutlined style={{ color: "#9aa5b8" }} />
            {[r.city, r.region].filter(Boolean).join(", ")}
          </Space>
        ) : (
          "—"
        ),
    },
    { title: "Channel", dataIndex: "channel", render: (v) => (v ? <Tag>{v}</Tag> : "—") },
    {
      title: "Devices",
      dataIndex: "deviceCount",
      align: "center",
      render: (n) => <Tag color={n > 0 ? "blue" : "default"}>{n || 0}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      render: (a) => (a === false ? <Tag color="red">Inactive</Tag> : <Tag color="green">Active</Tag>),
    },
    { title: "Updated", dataIndex: "updatedAt", sorter: true, render: (v) => (v ? relativeTime(v) : "—") },
    {
      title: "",
      key: "actions",
      align: "right",
      render: (_, r) => (
        <Button type="link" onClick={(e) => { e.stopPropagation(); open(r._id); }}>
          View
        </Button>
      ),
    },
  ];

  const toolbar = (
    <Space wrap>
      <Select
        allowClear
        showSearch
        placeholder="Location"
        style={{ minWidth: 170 }}
        value={region}
        onChange={setRegion}
        optionFilterProp="label"
        options={facets.regions.map((r) => ({ value: r, label: r }))}
      />
      <Select
        allowClear
        showSearch
        placeholder="Channel"
        style={{ minWidth: 130 }}
        value={channel}
        onChange={setChannel}
        optionFilterProp="label"
        options={facets.channels.map((c) => ({ value: String(c), label: String(c) }))}
      />
      <Select
        allowClear
        placeholder="Status"
        style={{ minWidth: 120 }}
        value={status}
        onChange={setStatus}
        options={STATUS_OPTIONS}
      />
    </Space>
  );

  return (
    <div>
      <PageHeader
        title="Outlets"
        subtitle="Retail shops & device locations"
        icon={<ShopOutlined />}
        extra={
          isHardwareAdmin && (
            <Button icon={<CloudDownloadOutlined />} loading={syncing} onClick={onSync}>
              Sync from Planomaster
            </Button>
          )
        }
      />

      <DataTable
        rowKey="_id"
        columns={columns}
        fetcher={listShops}
        refreshKey={refreshKey}
        searchPlaceholder="Search code, name or city…"
        toolbar={toolbar}
        filters={{ region, channel, isActive: status }}
        onRow={(r) => ({ onClick: () => open(r._id) })}
        emptyText="No outlets match these filters"
      />
    </div>
  );
}
